import ApiService from './api.service';
import type { AnalysisDimensionFilter } from '../utils/filterComparators';
import type { RestrictionCheck, RowRestriction } from './userGroup.service';

export type FronteggRole = {
  id: string;
  key: string;
  name: string;
};

export type TenantUser = {
  id: string;
  name: string;
  email: string;
  profilePictureUrl?: string;
  roles?: FronteggRole[];
};

export type ListUsersResponse = {
  records: TenantUser[];
};

export type UserSettings = {
  id?: string;
  frontegg_id: string;
  /** Older per-user filters; shown read-only, convertible to row_restrictions. */
  dimension_filters: AnalysisDimensionFilter[];
  row_restrictions: RowRestriction[];
  blacklisted_metric_ids: string[];
  date_dimension_variant: string | null;
  group_id: string | null;
};

export type UserSettingsResponse = {
  record: UserSettings;
};

export class UserService {
  async listTenantUsers(): Promise<TenantUser[]> {
    const result = await ApiService.getInstance().request(
      'GET',
      '/tenant-config/users'
    ) as ListUsersResponse;

    return result.records;
  }

  async getUserSettings(userFronteggId: string): Promise<UserSettings> {
    const result = await ApiService.getInstance().request(
      'GET',
      `/user-permissions/${userFronteggId}`
    ) as UserSettingsResponse;

    return result.record;
  }

  /** The saved settings, plus each row restriction's state across Production and Develop. */
  async updateUserSettings(
    userFronteggId: string,
    settings: Partial<Omit<UserSettings, 'id' | 'frontegg_id'>>
  ): Promise<UserSettings & { restriction_checks?: RestrictionCheck[] }> {
    const result = await ApiService.getInstance().request(
      'PUT',
      `/user-permissions/${userFronteggId}`,
      settings
    ) as UserSettingsResponse & { restriction_checks?: RestrictionCheck[] };

    return { ...result.record, restriction_checks: result.restriction_checks };
  }

  async getBlacklistedMetrics(userFronteggId: string): Promise<string[]> {
    const settings = await this.getUserSettings(userFronteggId);
    return settings.blacklisted_metric_ids || [];
  }

  async updateBlacklistedMetrics(userFronteggId: string, blacklistedMetricIds: string[]): Promise<string[]> {
    const settings = await this.updateUserSettings(userFronteggId, {
      blacklisted_metric_ids: blacklistedMetricIds,
    });
    return settings.blacklisted_metric_ids || [];
  }

  async getUserDimensionFilters(userFronteggId: string): Promise<AnalysisDimensionFilter[]> {
    const settings = await this.getUserSettings(userFronteggId);
    return settings.dimension_filters || [];
  }

  async updateUserDimensionFilters(userFronteggId: string, dimensionFilters: AnalysisDimensionFilter[]): Promise<AnalysisDimensionFilter[]> {
    const settings = await this.updateUserSettings(userFronteggId, {
      dimension_filters: dimensionFilters,
    });
    return settings.dimension_filters || [];
  }

  async listRoles(): Promise<FronteggRole[]> {
    const result = await ApiService.getInstance().request(
      'GET',
      '/tenant-config/roles'
    ) as { records: FronteggRole[] };
    return result.records;
  }

  async updateUserRoles(userId: string, roleIds: string[]): Promise<void> {
    await ApiService.getInstance().request(
      'PUT',
      `/tenant-config/users/${userId}/roles`,
      { role_ids: roleIds }
    );
  }
}

export const userService = new UserService();
